import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormData } from "../context/FormDataContext";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #444",
  outline: "none",
  fontSize: 14,
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 90,
  resize: "vertical",
};

const boxStyle = {
  border: "1px solid #333",
  borderRadius: 12,
  padding: 12,
  marginBottom: 12,
};

const btnStyle = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #444",
  cursor: "pointer",
  fontWeight: 900,
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 800,
  marginBottom: 6,
};

const toStr = (v) => String(v ?? "").trim();

function formatCzDate(v) {
  const s = toStr(v);
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${Number(d)}. ${Number(m)}. ${y}`;
  }
  return s;
}

function detectFemale(rec = {}) {
  const txt = `${toStr(rec.osloveni)} ${toStr(rec.jmenoZesnuleho)}`.toLowerCase();
  if (txt.includes("paní")) return true;
  return false;
}

function buildAutoRec(formData = {}) {
  const z = formData?.zesnulaOsoba || {};
  const v = formData?.vypravitel || {};
  const vi = formData?.vstupniInformace || {};
  const ob = formData?.obrad || {};
  const kp = formData?.kremacePohreb || {};

  const jmeno =
    [z.jmeno, z.prijmeni].filter(Boolean).join(" ").trim() ||
    [vi.jmenoZesnuleho, vi.prijmeniZesnuleho].filter(Boolean).join(" ").trim();

  const datumNarozeni = toStr(z.datumNarozeni || vi.datumNarozeniZesnuleho);
  const datumUmrti = toStr(z.datumUmrti || vi.datumUmrti);
  const mistoUmrti = toStr(z.mistoUmrti || vi.mistoUmrti || vi.adresaUmrti);

  const datumObradu = toStr(
    ob.datum || ob.datumObradu || kp.datumObradu || kp.obradDatum
  );
  const casObradu = toStr(
    ob.cas || ob.casObradu || ob.casOd || kp.casObradu || kp.obradCas
  );
  const mistoObradu = toStr(
    ob.misto || ob.mistoObradu || kp.mistoObradu
  );

  const vypravitel = [v.jmeno, v.prijmeni].filter(Boolean).join(" ").trim();

  return {
    jmenoZesnuleho: jmeno,
    osloveni: jmeno ? `pan/paní ${jmeno}` : "",
    datumNarozeni,
    mistoNarozeni: "",
    datumUmrti,
    mistoUmrti,
    datumObradu,
    casObradu,
    mistoObradu,
    vypravitel,
    uvod:
      jmeno
        ? `Dnes jsme se sešli, abychom se naposledy rozloučili s ${jmeno}.`
        : "Dnes jsme se sešli, abychom se naposledy rozloučili.",
    rodina: "",
    zivotniCesta: "",
    povaha: "",
    zaliby: "",
    duleziteOkamziky: "",
    textOdRodiny: "",
    poznamkaProRecnika: "",
    zaver:
      jmeno
        ? `Vzpomínejme na ${jmeno} s úctou, láskou a vděčností.`
        : "Vzpomínejme s úctou, láskou a vděčností.",
    stylReci: "classic",
    generovanyText: "",
    finalniText: "",
    pouzitFinalniText: false,
    generovaneVarianty: [],
  };
}

function mergeRecWithAuto(savedRec = {}, formData = {}) {
  const auto = buildAutoRec(formData);
  const out = { ...auto, ...(savedRec || {}) };

  for (const k of Object.keys(auto)) {
    if (k === "generovaneVarianty") continue;
    if (k === "pouzitFinalniText") continue;
    if (!toStr(out[k]) && typeof auto[k] === "string") out[k] = auto[k];
  }

  if (!Array.isArray(out.generovaneVarianty)) out.generovaneVarianty = [];
  out.pouzitFinalniText = out.pouzitFinalniText === true;
  out.stylReci = toStr(out.stylReci) || "classic";

  return out;
}

function recSig(rec = {}) {
  try {
    return JSON.stringify({
      jmenoZesnuleho: toStr(rec.jmenoZesnuleho),
      osloveni: toStr(rec.osloveni),
      datumNarozeni: toStr(rec.datumNarozeni),
      mistoNarozeni: toStr(rec.mistoNarozeni),
      datumUmrti: toStr(rec.datumUmrti),
      mistoUmrti: toStr(rec.mistoUmrti),
      datumObradu: toStr(rec.datumObradu),
      casObradu: toStr(rec.casObradu),
      mistoObradu: toStr(rec.mistoObradu),
      vypravitel: toStr(rec.vypravitel),
      uvod: toStr(rec.uvod),
      rodina: toStr(rec.rodina),
      zivotniCesta: toStr(rec.zivotniCesta),
      povaha: toStr(rec.povaha),
      zaliby: toStr(rec.zaliby),
      duleziteOkamziky: toStr(rec.duleziteOkamziky),
      textOdRodiny: toStr(rec.textOdRodiny),
      poznamkaProRecnika: toStr(rec.poznamkaProRecnika),
      zaver: toStr(rec.zaver),
      stylReci: toStr(rec.stylReci),
      generovanyText: toStr(rec.generovanyText),
      finalniText: toStr(rec.finalniText),
      pouzitFinalniText: rec.pouzitFinalniText === true,
      generovaneVarianty: Array.isArray(rec.generovaneVarianty)
        ? rec.generovaneVarianty.map((x) => ({
            id: toStr(x?.id),
            title: toStr(x?.title),
            text: toStr(x?.text),
          }))
        : [],
    });
  } catch {
    return "";
  }
}

function speechBase(rec = {}) {
  const jmeno = toStr(rec.jmenoZesnuleho);
  const osloveni = toStr(rec.osloveni) || jmeno;
  const datumNar = formatCzDate(rec.datumNarozeni);
  const mistoNar = toStr(rec.mistoNarozeni);
  const datumUmrti = formatCzDate(rec.datumUmrti);
  const mistoUmrti = toStr(rec.mistoUmrti);
  const datumObradu = formatCzDate(rec.datumObradu);
  const casObradu = toStr(rec.casObradu);
  const mistoObradu = toStr(rec.mistoObradu);
  const uvod = toStr(rec.uvod);
  const rodina = toStr(rec.rodina);
  const zivotniCesta = toStr(rec.zivotniCesta);
  const povaha = toStr(rec.povaha);
  const zaliby = toStr(rec.zaliby);
  const dulezite = toStr(rec.duleziteOkamziky);
  const odRodiny = toStr(rec.textOdRodiny);
  const zaver = toStr(rec.zaver);
  const female = detectFemale(rec);

  return {
    jmeno,
    osloveni,
    datumNar,
    mistoNar,
    datumUmrti,
    mistoUmrti,
    datumObradu,
    casObradu,
    mistoObradu,
    uvod,
    rodina,
    zivotniCesta,
    povaha,
    zaliby,
    dulezite,
    odRodiny,
    zaver,
    female,
    narodil: female ? "narodila se" : "narodil se",
    zemrel: female ? "Její životní cesta se uzavřela" : "Jeho životní cesta se uzavřela",
    mel: female ? "měla" : "měl",
    pusobil: female ? "působila" : "působil",
    zanechala: female ? "zanechala" : "zanechal",
  };
}

function composeSpeechClassic(rec = {}) {
  const b = speechBase(rec);
  const out = [];

  if (b.uvod) out.push(b.uvod);
  else if (b.jmeno) out.push(`Dnes jsme se sešli, abychom se naposledy rozloučili s ${b.jmeno}.`);

  if (b.osloveni && (b.datumNar || b.mistoNar || b.datumUmrti || b.mistoUmrti)) {
    let veta = `${b.osloveni}`;
    if (b.datumNar && b.mistoNar) veta += ` ${b.narodil} ${b.datumNar} v ${b.mistoNar}.`;
    else if (b.datumNar) veta += ` ${b.narodil} ${b.datumNar}.`;
    else if (b.mistoNar) veta += ` ${b.narodil} v ${b.mistoNar}.`;
    else veta += ".";

    if (b.datumUmrti && b.mistoUmrti) veta += ` ${b.zemrel} ${b.datumUmrti} v ${b.mistoUmrti}.`;
    else if (b.datumUmrti) veta += ` ${b.zemrel} ${b.datumUmrti}.`;
    else if (b.mistoUmrti) veta += ` ${b.zemrel} v ${b.mistoUmrti}.`;

    out.push(veta.trim());
  }

  if (b.rodina) out.push(`Rodina pro něj představovala zázemí, blízkost i jistotu. ${b.rodina}`);
  if (b.zivotniCesta) out.push(`Každý lidský život je tvořen prací, vztahy, radostmi i těžšími chvílemi. Také život ${b.jmeno || "zesnulého"} nesl mnoho zkušeností a příběhů. ${b.zivotniCesta}`);
  if (b.povaha) out.push(`Ti, kdo ${b.jmeno || "zesnulého"} znali, si jej budou pamatovat i podle toho, jak na své okolí ${b.pusobil}. ${b.povaha}`);
  if (b.zaliby) out.push(`Vedle každodenních povinností ${b.mel} také to, co přinášelo radost a naplnění. ${b.zaliby}`);
  if (b.dulezite) out.push(`V životě každého člověka zůstávají okamžiky, na které nelze zapomenout. U ${b.jmeno || "zesnulého"} k nim patřilo zejména toto: ${b.dulezite}`);
  if (b.odRodiny) out.push(`Nejsilnější vzpomínky si nesou v srdci především nejbližší. Rodina vzpomíná těmito slovy: ${b.odRodiny}`);

  const obradBits = [b.datumObradu, b.casObradu, b.mistoObradu].filter(Boolean);
  if (obradBits.length) out.push(`Dnes se loučíme při smutečním obřadu ${obradBits.join(", ")} a v tiché vzpomínce děkujeme za vše, co zde po sobě ${b.zanechala}.`);

  if (b.zaver) out.push(b.zaver);
  else if (b.jmeno) out.push(`Zachovejme ${b.jmeno} v našich vzpomínkách s úctou, láskou a vděčností.`);

  return out.filter(Boolean).join("\n\n").trim();
}

function composeSpeechWarm(rec = {}) {
  const b = speechBase(rec);
  const out = [];

  out.push(
    b.uvod ||
      (b.jmeno
        ? `Dnes jsme se sešli, abychom se s úctou a vděčností rozloučili s ${b.jmeno}.`
        : "Dnes jsme se sešli, abychom se s úctou a vděčností rozloučili.")
  );

  if (b.osloveni && (b.datumNar || b.datumUmrti || b.mistoUmrti)) {
    let veta = `${b.osloveni}`;
    if (b.datumNar && b.mistoNar) veta += ` ${b.narodil} ${b.datumNar} v ${b.mistoNar}.`;
    else if (b.datumNar) veta += ` ${b.narodil} ${b.datumNar}.`;
    if (b.datumUmrti && b.mistoUmrti) veta += ` ${b.zemrel} ${b.datumUmrti} v ${b.mistoUmrti}.`;
    else if (b.datumUmrti) veta += ` ${b.zemrel} ${b.datumUmrti}.`;
    out.push(veta.trim());
  }

  if (b.rodina) out.push(`Největší hodnotu v životě často představují lidé, které máme rádi. Tak tomu bylo i zde. ${b.rodina}`);
  if (b.zivotniCesta) out.push(`Život ${b.jmeno || "zesnulého"} nebyl jen sledem dnů, ale příběhem naplněným zkušenostmi, prací i setkáními s druhými. ${b.zivotniCesta}`);
  if (b.povaha) out.push(`V srdcích blízkých zůstane zachováno i to, jaký ${b.jmeno || "byl"} ${b.pusobil} na své okolí. ${b.povaha}`);
  if (b.zaliby) out.push(`Radost a lidské teplo často přicházejí i z malých věcí, které člověka těší. ${b.zaliby}`);
  if (b.dulezite) out.push(`Mezi chvíle, které budou s tímto životem navždy spojeny, patří i tyto okamžiky: ${b.dulezite}`);
  if (b.odRodiny) out.push(`Rodina uchovává vzpomínku těmito slovy: ${b.odRodiny}`);

  const obradBits = [b.datumObradu, b.casObradu, b.mistoObradu].filter(Boolean);
  if (obradBits.length) out.push(`Dnes se loučíme při smutečním obřadu ${obradBits.join(", ")} a v duchu děkujeme za vše dobré, co zde po sobě ${b.zanechala}.`);

  out.push(b.zaver || (b.jmeno ? `Vzpomínejme na ${b.jmeno} s láskou, úctou a vděčností.` : "Vzpomínejme s láskou, úctou a vděčností."));
  return out.filter(Boolean).join("\n\n").trim();
}

function composeSpeechFormal(rec = {}) {
  const b = speechBase(rec);
  const out = [];

  out.push(
    b.uvod ||
      (b.jmeno
        ? `Sešli jsme se dnes, abychom uctili památku ${b.jmeno} a naposledy se s ním rozloučili.`
        : "Sešli jsme se dnes, abychom uctili památku zesnulého a naposledy se rozloučili.")
  );

  if (b.osloveni && (b.datumNar || b.mistoNar || b.datumUmrti || b.mistoUmrti)) {
    let veta = `${b.osloveni}`;
    if (b.datumNar && b.mistoNar) veta += ` ${b.narodil} ${b.datumNar} v ${b.mistoNar}.`;
    else if (b.datumNar) veta += ` ${b.narodil} ${b.datumNar}.`;
    else if (b.mistoNar) veta += ` ${b.narodil} v ${b.mistoNar}.`;
    else veta += ".";

    if (b.datumUmrti && b.mistoUmrti) veta += ` ${b.zemrel} ${b.datumUmrti} v ${b.mistoUmrti}.`;
    else if (b.datumUmrti) veta += ` ${b.zemrel} ${b.datumUmrti}.`;
    else if (b.mistoUmrti) veta += ` ${b.zemrel} v ${b.mistoUmrti}.`;

    out.push(veta.trim());
  }

  if (b.zivotniCesta) out.push(`Jeho život byl naplněn prací, zkušenostmi a událostmi, které formovaly jeho osobnost i vztah k druhým lidem. ${b.zivotniCesta}`);
  if (b.rodina) out.push(`Významné místo v tomto životním příběhu zaujímala také rodina. ${b.rodina}`);
  if (b.povaha) out.push(`Ve vzpomínkách blízkých zůstane zachováno i to, jak ${b.pusobil} na své okolí. ${b.povaha}`);
  if (b.zaliby) out.push(`Součástí života byly rovněž záliby a činnosti, které přinášely radost a vnitřní naplnění. ${b.zaliby}`);
  if (b.dulezite) out.push(`K významným životním okamžikům patřilo zejména toto: ${b.dulezite}`);
  if (b.odRodiny) out.push(`Rodina a nejbližší vzpomínají těmito slovy: ${b.odRodiny}`);

  const obradBits = [b.datumObradu, b.casObradu, b.mistoObradu].filter(Boolean);
  if (obradBits.length) out.push(`Dnes se loučíme při smutečním obřadu ${obradBits.join(", ")} a s úctou děkujeme za vše, co zde po sobě ${b.zanechala}.`);

  out.push(b.zaver || (b.jmeno ? `Čest jeho památce. Zachovejme ${b.jmeno} v úctě a ve vzpomínkách.` : "Čest jeho památce."));
  return out.filter(Boolean).join("\n\n").trim();
}

function buildSpeechVariants(rec = {}) {
  return [
    { id: "classic", title: "Návrh A – pietní / klasický", text: composeSpeechClassic(rec) },
    { id: "warm", title: "Návrh B – osobní / srdečný", text: composeSpeechWarm(rec) },
    { id: "formal", title: "Návrh C – slavnostnější", text: composeSpeechFormal(rec) },
  ];
}

export default function RecForm() {
  const { formData, mergeFormData, updateFormData, currentOrderId, saveOrder } = useFormData();
  const saveFormData =
    typeof updateFormData === "function" ? updateFormData : mergeFormData;

  const __orderId = String(
    currentOrderId ||
      formData?.orderId ||
      formData?.id ||
      formData?.zakazkaId ||
      ""
  ).trim();

  const initial = useMemo(() => {
    return mergeRecWithAuto(formData?.rec || {}, formData || {});
  }, [formData?.rec, formData]);

  const [recData, setRecData] = useState(initial);
  const [saved, setSaved] = useState(false);

  const hydratingRef = useRef(false);
  const touchedRef = useRef(false);
  const lastAutoSigRef = useRef("");

  const finalTextPreview = useMemo(() => {
    if (recData.pouzitFinalniText && toStr(recData.finalniText)) return toStr(recData.finalniText);
    if (toStr(recData.generovanyText)) return toStr(recData.generovanyText);
    return composeSpeechClassic(recData);
  }, [recData]);

  const setField = (key, value) => {
    setRecData((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    touchedRef.current = true;
  };

  const buildPayload = () => {
    return {
      jmenoZesnuleho: toStr(recData.jmenoZesnuleho),
      osloveni: toStr(recData.osloveni),
      datumNarozeni: toStr(recData.datumNarozeni),
      mistoNarozeni: toStr(recData.mistoNarozeni),
      datumUmrti: toStr(recData.datumUmrti),
      mistoUmrti: toStr(recData.mistoUmrti),
      datumObradu: toStr(recData.datumObradu),
      casObradu: toStr(recData.casObradu),
      mistoObradu: toStr(recData.mistoObradu),
      vypravitel: toStr(recData.vypravitel),
      uvod: toStr(recData.uvod),
      rodina: toStr(recData.rodina),
      zivotniCesta: toStr(recData.zivotniCesta),
      povaha: toStr(recData.povaha),
      zaliby: toStr(recData.zaliby),
      duleziteOkamziky: toStr(recData.duleziteOkamziky),
      textOdRodiny: toStr(recData.textOdRodiny),
      poznamkaProRecnika: toStr(recData.poznamkaProRecnika),
      zaver: toStr(recData.zaver),
      stylReci: toStr(recData.stylReci) || "classic",
      generovanyText: toStr(recData.generovanyText),
      finalniText: toStr(recData.finalniText),
      pouzitFinalniText: recData.pouzitFinalniText === true,
      generovaneVarianty: Array.isArray(recData.generovaneVarianty)
        ? recData.generovaneVarianty.map((x) => ({
            id: toStr(x?.id),
            title: toStr(x?.title),
            text: toStr(x?.text),
          }))
        : [],
    };
  };

  const __knihaSaveNow = async (fd) => {
    try {
      if (typeof saveOrder !== "function") {
        throw new Error("Chybí saveOrder() v useFormData().");
      }
    
      const out = await saveOrder();
      console.log("REC saveOrder ok:", out);
    } catch (e) {
      console.warn("REC saveOrder failed:", String(e?.message || e));
      throw e;
    }
  };

  const handleNatáhnoutZApp = () => {
    const auto = buildAutoRec(formData || {});
    setRecData((prev) => ({
      ...prev,
      jmenoZesnuleho: toStr(prev.jmenoZesnuleho) || auto.jmenoZesnuleho,
      osloveni: toStr(prev.osloveni) || auto.osloveni,
      datumNarozeni: toStr(prev.datumNarozeni) || auto.datumNarozeni,
      datumUmrti: toStr(prev.datumUmrti) || auto.datumUmrti,
      mistoUmrti: toStr(prev.mistoUmrti) || auto.mistoUmrti,
      datumObradu: toStr(prev.datumObradu) || auto.datumObradu,
      casObradu: toStr(prev.casObradu) || auto.casObradu,
      mistoObradu: toStr(prev.mistoObradu) || auto.mistoObradu,
      vypravitel: toStr(prev.vypravitel) || auto.vypravitel,
      uvod: toStr(prev.uvod) || auto.uvod,
      zaver: toStr(prev.zaver) || auto.zaver,
    }));
    setSaved(false);
    touchedRef.current = true;
  };

  const handleGenerateVariants = () => {
    const variants = buildSpeechVariants(recData);
    const chosen =
      variants.find((x) => x.id === recData.stylReci)?.text ||
      variants[0]?.text ||
      "";

    setRecData((prev) => ({
      ...prev,
      generovaneVarianty: variants,
      generovanyText: chosen,
      finalniText: toStr(prev.finalniText) || chosen,
    }));
    setSaved(false);
    touchedRef.current = true;
  };

  const handleUseVariant = (variant) => {
    const text = toStr(variant?.text);
    setRecData((prev) => ({
      ...prev,
      stylReci: toStr(variant?.id) || prev.stylReci,
      generovanyText: text,
      finalniText: text,
      pouzitFinalniText: true,
    }));
    setSaved(false);
    touchedRef.current = true;
  };

  const handleSave = async () => {
    if (!__orderId) {
      try {
        alert("Zakázka ještě není načtená (orderId je prázdné).");
      } catch {}
      return;
    }

    const payloadRec = buildPayload();

    try {
      saveFormData({ rec: payloadRec }, "rec:manual");
    } catch {}

    try {
      const id = String(__orderId).trim();
      if (id) {
        const fd2 = {
          ...(formData || {}),
          id,
          orderId: id,
          currentOrderId: id,
          zakazkaId: id,
          rec: payloadRec,
        };
        await __knihaSaveNow(fd2);
      }
    } catch {}

    try {
      lastAutoSigRef.current = recSig(payloadRec);
    } catch {}

    setSaved(true);
    touchedRef.current = false;
  };

  useEffect(() => {
    const incoming = mergeRecWithAuto(formData?.rec || {}, formData || {});
    const incomingSig = recSig(incoming);
    const localSig = recSig(recData);

    if (!incomingSig || incomingSig === localSig) return;

    hydratingRef.current = true;
    lastAutoSigRef.current = incomingSig;
    setRecData(incoming);

    setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, [__orderId, formData?.rec, formData]);

  useEffect(() => {
    if (!__orderId) return;
    if (!touchedRef.current) return;
    if (hydratingRef.current) return;

    const nextSig = recSig(recData);
    if (!nextSig || nextSig === lastAutoSigRef.current) return;

    const curSig = recSig(formData?.rec || {});
    if (nextSig === curSig) {
      lastAutoSigRef.current = nextSig;
      return;
    }

    const t = setTimeout(() => {
      const nextSig2 = recSig(recData);
      if (!nextSig2 || nextSig2 === lastAutoSigRef.current) return;

      const curSig2 = recSig(formData?.rec || {});
      if (nextSig2 === curSig2) {
        lastAutoSigRef.current = nextSig2;
        touchedRef.current = false;
        return;
      }

      lastAutoSigRef.current = nextSig2;

      try {
        mergeFormData({ rec: buildPayload() }, "rec:auto");
      } catch {}
    }, 350);

    return () => clearTimeout(t);
  }, [__orderId, recData, formData?.rec, mergeFormData]);

  return (
    <div>
      <h2 style={{ margin: "8px 0 14px" }}>🗣️ Řeč</h2>

      <div style={boxStyle}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <button type="button" style={btnStyle} onClick={handleNatáhnoutZApp}>
            Natáhnout údaje z aplikace
          </button>
          <button type="button" style={btnStyle} onClick={handleGenerateVariants}>
            Vygenerovat více návrhů
          </button>
          <button type="button" style={btnStyle} onClick={handleSave}>
            Uložit řeč
          </button>
          {saved ? <span style={{ fontWeight: 900, color: "limegreen" }}>✅ Uloženo</span> : null}
        </div>

        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <div>
            <div style={labelStyle}>Jméno zesnulého</div>
            <input style={inputStyle} value={recData.jmenoZesnuleho || ""} onChange={(e) => setField("jmenoZesnuleho", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Oslovení / jak mluvit o zesnulém</div>
            <input style={inputStyle} value={recData.osloveni || ""} onChange={(e) => setField("osloveni", e.target.value)} placeholder="např. pan Jan Novák / paní Anna Nováková" />
          </div>

          <div>
            <div style={labelStyle}>Typ návrhu řeči</div>
            <select
              style={inputStyle}
              value={recData.stylReci || "classic"}
              onChange={(e) => setField("stylReci", e.target.value)}
            >
              <option value="classic">Pietní / klasický</option>
              <option value="warm">Osobní / srdečný</option>
              <option value="formal">Slavnostnější</option>
            </select>
          </div>

          <div>
            <div style={labelStyle}>Vypravitel</div>
            <input style={inputStyle} value={recData.vypravitel || ""} onChange={(e) => setField("vypravitel", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Datum narození</div>
            <input style={inputStyle} value={recData.datumNarozeni || ""} onChange={(e) => setField("datumNarozeni", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Místo narození</div>
            <input style={inputStyle} value={recData.mistoNarozeni || ""} onChange={(e) => setField("mistoNarozeni", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Datum úmrtí</div>
            <input style={inputStyle} value={recData.datumUmrti || ""} onChange={(e) => setField("datumUmrti", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Místo úmrtí</div>
            <input style={inputStyle} value={recData.mistoUmrti || ""} onChange={(e) => setField("mistoUmrti", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Datum obřadu</div>
            <input style={inputStyle} value={recData.datumObradu || ""} onChange={(e) => setField("datumObradu", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Čas obřadu</div>
            <input style={inputStyle} value={recData.casObradu || ""} onChange={(e) => setField("casObradu", e.target.value)} />
          </div>

          <div>
            <div style={labelStyle}>Místo obřadu</div>
            <input style={inputStyle} value={recData.mistoObradu || ""} onChange={(e) => setField("mistoObradu", e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Úvod řeči</div>
          <textarea style={textareaStyle} value={recData.uvod || ""} onChange={(e) => setField("uvod", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Rodina</div>
          <textarea style={textareaStyle} value={recData.rodina || ""} onChange={(e) => setField("rodina", e.target.value)} placeholder="např. manželka, děti, vnoučata, sourozenci..." />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Životní cesta / zaměstnání</div>
          <textarea style={textareaStyle} value={recData.zivotniCesta || ""} onChange={(e) => setField("zivotniCesta", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Povaha</div>
          <textarea style={textareaStyle} value={recData.povaha || ""} onChange={(e) => setField("povaha", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Záliby / koníčky</div>
          <textarea style={textareaStyle} value={recData.zaliby || ""} onChange={(e) => setField("zaliby", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Důležité životní momenty</div>
          <textarea style={textareaStyle} value={recData.duleziteOkamziky || ""} onChange={(e) => setField("duleziteOkamziky", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Text od rodiny</div>
          <textarea style={textareaStyle} value={recData.textOdRodiny || ""} onChange={(e) => setField("textOdRodiny", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Poznámka pro řečníka</div>
          <textarea style={textareaStyle} value={recData.poznamkaProRecnika || ""} onChange={(e) => setField("poznamkaProRecnika", e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Závěr</div>
          <textarea style={textareaStyle} value={recData.zaver || ""} onChange={(e) => setField("zaver", e.target.value)} />
        </div>
      </div>

      <div style={boxStyle}>
        <div style={{ ...labelStyle, marginBottom: 10 }}>Vygenerované návrhy řeči</div>

        {!Array.isArray(recData.generovaneVarianty) || recData.generovaneVarianty.length === 0 ? (
          <div style={{ opacity: 0.8 }}>Zatím nejsou vygenerované varianty. Klikni na „Vygenerovat více návrhů“.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {recData.generovaneVarianty.map((variant) => (
              <div key={variant.id} style={{ border: "1px solid #444", borderRadius: 10, padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                  <div style={{ fontWeight: 900 }}>{variant.title}</div>
                  <button type="button" style={btnStyle} onClick={() => handleUseVariant(variant)}>
                    Použít tento návrh
                  </button>
                </div>
                <pre style={{ whiteSpace: "pre-wrap", opacity: 0.95, lineHeight: 1.5, margin: 0 }}>
                  {variant.text}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={boxStyle}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>Automaticky vybraný návrh</div>
        <pre style={{ whiteSpace: "pre-wrap", opacity: 0.95, lineHeight: 1.5 }}>
          {toStr(recData.generovanyText) || "Zatím nevygenerováno."}
        </pre>

        <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800, marginTop: 10 }}>
          <input
            type="checkbox"
            checked={recData.pouzitFinalniText === true}
            onChange={(e) => setField("pouzitFinalniText", e.target.checked)}
          />
          Použít ručně upravený finální text pro dokument
        </label>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Finální text pro řečníka</div>
          <textarea
            style={{ ...textareaStyle, minHeight: 240 }}
            value={recData.finalniText || ""}
            onChange={(e) => setField("finalniText", e.target.value)}
            placeholder="Sem můžeš ručně doplnit nebo upravit finální podobu řeči."
          />
        </div>
      </div>

      <div style={boxStyle}>
        <div style={labelStyle}>Náhled finálního textu, který půjde do dokumentu</div>
        <pre style={{ whiteSpace: "pre-wrap", opacity: 0.95, lineHeight: 1.5 }}>
          {finalTextPreview}
        </pre>
      </div>

      <div style={boxStyle}>
        <div style={labelStyle}>Debug (ukládá se do formData.rec)</div>
        <pre style={{ whiteSpace: "pre-wrap", opacity: 0.85 }}>
          {JSON.stringify(recData, null, 2)}
        </pre>
      </div>
    </div>
  );
}
